// API routes exports
import type { Hono } from 'hono';
import { createApiResponse, createErrorResponse } from '../middleware/index.js';
import { extractBearerToken, verifyToken } from '../auth/index.js';
import type { ApiServer } from '../server/index.js';

export function setupRoutes(app: ApiServer) {
  // Public endpoints
  app.get('/api/v1/ready', (c) => {
    return c.json(createApiResponse({ ready: true }));
  });

  app.get('/api/v1/signals/top', (c) => {
    // Limited public access - top 5 signals only
    const mockSignals = [
      { token: 'BTC', signal: 'bullish', confidence: 0.85, timestamp: new Date().toISOString() },
      { token: 'ETH', signal: 'neutral', confidence: 0.62, timestamp: new Date().toISOString() }
    ];
    
    return c.json(createApiResponse(mockSignals.slice(0, 5)));
  });

  app.get('/api/v1/tokens/trending', (c) => {
    // Limited public access - trending tokens
    const mockTrending = [
      { symbol: 'BTC', mentions: 1250, sentiment: 0.72, riskLevel: 0.1 },
      { symbol: 'ETH', mentions: 890, sentiment: 0.65, riskLevel: 0.15 }
    ];
    
    return c.json(createApiResponse(mockTrending.slice(0, 3)));
  });

  // Bot detection endpoint (requires authentication)
  app.post('/api/v1/analyze/bot-detection', async (c) => {
    const authHeader = c.req.header('Authorization');
    const token = extractBearerToken(authHeader);
    
    if (!token) {
      return c.json(createErrorResponse(401, 'Unauthorized', 'Bearer token required'), 401);
    }
    
    const user = verifyToken(token);
    if (!user) {
      return c.json(createErrorResponse(401, 'Unauthorized', 'Invalid token'), 401);
    }

    try {
      const body = await c.req.json();
      const { username, maxTweets = 50 } = body;
      
      if (!username) {
        return c.json(createErrorResponse(400, 'Bad Request', 'Username is required'), 400);
      }

      // Mock bot detection response
      const mockAnalysis = {
        username,
        botProbability: Math.random() * 0.6 + 0.2, // 0.2-0.8
        suspiciousReplies: Math.floor(Math.random() * 20),
        networkRisk: Math.random() * 0.4 + 0.1, // 0.1-0.5
        alerts: [
          {
            type: 'coordinated_activity',
            severity: 'medium',
            description: 'Potential coordinated behavior detected'
          }
        ],
        recommendations: [
          'Monitor account for coordinated activity patterns',
          'Check reply authenticity and engagement quality'
        ]
      };

      return c.json(createApiResponse(mockAnalysis));
      
    } catch (error) {
      return c.json(createErrorResponse(500, 'Internal Server Error', 'Analysis failed'), 500);
    }
  });

  // Token shilling detection endpoint
  app.post('/api/v1/analyze/token-shilling', async (c) => {
    const authHeader = c.req.header('Authorization');
    const token = extractBearerToken(authHeader);
    
    if (!token) {
      return c.json(createErrorResponse(401, 'Unauthorized', 'Bearer token required'), 401);
    }
    
    const user = verifyToken(token);
    if (!user || user.tier === 'free') {
      return c.json(createErrorResponse(403, 'Forbidden', 'Premium access required'), 403);
    }

    try {
      const body = await c.req.json();
      const { username, tokenSymbol } = body;
      
      if (!username || !tokenSymbol) {
        return c.json(createErrorResponse(400, 'Bad Request', 'Username and tokenSymbol are required'), 400);
      }

      // Mock token shilling analysis
      const mockShillAnalysis = {
        username,
        tokenSymbol,
        shillProbability: Math.random() * 0.8 + 0.1,
        campaignType: 'pump_and_dump',
        riskLevel: Math.random() * 0.9 + 0.1,
        indicators: [
          'Coordinated timing patterns detected',
          'Generic promotional content found',
          'Suspicious interaction networks'
        ],
        alerts: [
          {
            alertType: 'coordinated_shilling',
            severity: 'high',
            confidence: 0.87,
            tokenSymbol
          }
        ]
      };

      return c.json(createApiResponse(mockShillAnalysis));
      
    } catch (error) {
      return c.json(createErrorResponse(500, 'Internal Server Error', 'Token analysis failed'), 500);
    }
  });

  // Premium features endpoint
  app.get('/api/v1/premium/dashboard', async (c) => {
    const authHeader = c.req.header('Authorization');
    const token = extractBearerToken(authHeader);
    
    if (!token) {
      return c.json(createErrorResponse(401, 'Unauthorized', 'Bearer token required'), 401);
    }
    
    const user = verifyToken(token);
    if (!user || user.tier === 'free') {
      return c.json(createErrorResponse(403, 'Forbidden', 'Premium access required'), 403);
    }

    const dashboardData = {
      accountsMonitored: 1247,
      botsDetected: 89,
      riskTokens: 12,
      activeAlerts: 3,
      recentActivity: [
        { type: 'bot_detected', username: 'user123', timestamp: new Date().toISOString() },
        { type: 'token_alert', token: 'SCAM', severity: 'high', timestamp: new Date().toISOString() }
      ]
    };

    return c.json(createApiResponse(dashboardData));
  });

  return app;
}

export { setupRoutes as default };